import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthenticateWithRedirectCallback } from '@clerk/clerk-react';
import { LoadingSpinner } from './LoadingSpinner';
import { AuthGate } from './AuthGate';
import { AdminGate } from './AdminGate';
import { RoleGate } from './RoleGate';

// Static Pages (Keep only Home for fast initial LCP)
import { Home } from '../pages/Home';

import { lazyRetry } from '../utils/lazyLoad';

// Lazy Loaded Pages
const Marketplace = lazyRetry(() => import('../pages/Marketplace').then(m => ({ default: m.Marketplace })), 'Marketplace');
const MarketplaceHub = lazyRetry(() => import('../pages/MarketplaceHub').then(m => ({ default: m.MarketplaceHub })), 'MarketplaceHub');
const IntentLandingPage = lazyRetry(() => import('../pages/IntentLandingPage').then(m => ({ default: m.IntentLandingPage })), 'IntentLandingPage');
const Login = lazyRetry(() => import('../pages/Login').then(m => ({ default: m.Login })), 'Login');
const SignUp = lazyRetry(() => import('../pages/SignUp').then(m => ({ default: m.SignUpPage })), 'SignUp');
const AdminLogin = lazyRetry(() => import('../pages/AdminLogin').then(m => ({ default: m.AdminLogin })), 'AdminLogin');

// Lazy Loaded Pages (Heavy or internal pages)
const AdminDashboard = lazyRetry(() => import('../pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })), 'AdminDashboard');
const Dashboard = lazyRetry(() => import('../pages/Dashboard').then(m => ({ default: m.Dashboard })), 'Dashboard');
const BlogDetail = lazyRetry(() => import('../pages/BlogDetail').then(m => ({ default: m.BlogDetail })), 'BlogDetail');
const Profile = lazyRetry(() => import('../pages/Profile').then(m => ({ default: m.Profile })), 'Profile');
const AddListing = lazyRetry(() => import('../pages/AddListing').then(m => ({ default: m.AddListing })), 'AddListing');
const ListingDetail = lazyRetry(() => import('../pages/ListingDetail').then(m => ({ default: m.ListingDetail })), 'ListingDetail');
const ListingRedirector = lazyRetry(() => import('./ListingRedirector').then(m => ({ default: m.ListingRedirector })), 'ListingRedirector');
const Blog = lazyRetry(() => import('../pages/Blog').then(m => ({ default: m.Blog })), 'Blog');

const Legal = lazyRetry(() => import('../pages/Legal').then(m => ({ default: m.Legal })), 'Legal');
const HowItWorks = lazyRetry(() => import('../pages/HowItWorks').then(m => ({ default: m.HowItWorks })), 'HowItWorks');
const Verification = lazyRetry(() => import('../pages/Verification').then(m => ({ default: m.Verification })), 'Verification');
const Pricing = lazyRetry(() => import('../pages/Pricing').then(m => ({ default: m.Pricing })), 'Pricing');
const Vendors = lazyRetry(() => import('../pages/Vendors').then(m => ({ default: m.Vendors })), 'Vendors');
const VendorDetail = lazyRetry(() => import('../pages/VendorDetail').then(m => ({ default: m.VendorDetail })), 'VendorDetail');
const AddVendor = lazyRetry(() => import('../pages/AddVendor').then(m => ({ default: m.AddVendor })), 'AddVendor');
const ListingSitemap = lazyRetry(() => import('../pages/ListingSitemap').then(m => ({ default: m.ListingSitemap })), 'ListingSitemap');
const DemandDetail = lazyRetry(() => import('../pages/DemandDetail').then(m => ({ default: m.DemandDetail })), 'DemandDetail');
const DataReport = lazyRetry(() => import('../pages/DataReport').then(m => ({ default: m.DataReport })), 'DataReport');

// Fallback 404 Component
const NotFound = () => (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-stone-400">
        <h2 className="text-4xl font-bold mb-4 text-primary">404</h2>
        <p>Stránka sa nenašla</p>
    </div>
);


// SSO callback handler for Google OAuth
const SsoCallback = () => <AuthenticateWithRedirectCallback />;

export const AppRoutes: React.FC = () => {
    const navigate = useNavigate();

    // Handle legacy hash-based SSO callbacks and legacy email hash routes (e.g. /#/profile, /#/dashboard)
    React.useEffect(() => {
        const hash = window.location.hash;
        if (hash.includes('sso-callback')) {
            // Strip hash and redirect to clean URL
            const cleanUrl = window.location.origin + window.location.pathname + window.location.search;
            window.history.replaceState(null, '', cleanUrl);
            window.location.reload();
            return;
        }

        // Handle legacy hash URLs from older emails (e.g. /#/profile, /#/dashboard, /#/market)
        if (hash.startsWith('#/')) {
            const target = hash.slice(1);
            navigate(target, { replace: true });
        }
    }, [navigate]);

    return (
        <Suspense fallback={<LoadingSpinner />}>
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/market" element={<Marketplace />} />
                <Route path="/market/mesto/:citySlug" element={<MarketplaceHub />} />
                <Route path="/market/mesto/:citySlug/:categorySlug" element={<MarketplaceHub />} />
                <Route path="/market/kategoria/:categorySlug" element={<MarketplaceHub />} />
                <Route path="/oslava/:intentSlug" element={<IntentLandingPage />} />
                <Route path="/report/eventove-priestory-2026" element={<DataReport />} />
                <Route path="/sluzby" element={<Vendors />} />
                <Route path="/sluzby/pridat" element={<AuthGate><AddVendor /></AuthGate>} />
                <Route path="/sluzby/upravit/:id" element={<AuthGate><AddVendor /></AuthGate>} />
                <Route path="/sluzby/:id" element={<VendorDetail />} />
                <Route path="/login/*" element={<Login />} />
                <Route path="/signup/*" element={<SignUp />} />
                <Route path="/sso-callback" element={<SsoCallback />} />

                {/* Protected Routes */}
                <Route path="/add" element={<AuthGate><RoleGate role="owner"><AddListing /></RoleGate></AuthGate>} />
                <Route path="/add/:id" element={<AuthGate><RoleGate role="owner"><AddListing /></RoleGate></AuthGate>} />
                <Route path="/profile" element={<AuthGate><Profile /></AuthGate>} />

                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:id" element={<BlogDetail />} />
                <Route path="/priestor/:id" element={<ListingDetail />} />
                <Route path="/priestor/:id/" element={<ListingDetail />} />
                
                {/* Legacy Listing Routes (Redirects to /priestor) */}
                <Route path="/listing/:id" element={<ListingRedirector />} />
                <Route path="/listing/:id/" element={<ListingRedirector />} />
                <Route path="/listing" element={<Navigate to="/market" replace />} />
                <Route path="/listing/" element={<Navigate to="/market" replace />} />

                {/* Admin & Dashboard */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminGate><AdminDashboard /></AdminGate>} />
                <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
                <Route path="/dashboard" element={<AuthGate><Dashboard /></AuthGate>} />
                <Route path="/demand/:id" element={<DemandDetail />} />
                <Route path="/demand/:id/" element={<DemandDetail />} />
                <Route path="/dopyt/:id" element={<DemandDetail />} />
                <Route path="/dopyt/:id/" element={<DemandDetail />} />
                <Route path="/dopyt" element={<Navigate to="/market?demands=1&new=1" replace />} />
                <Route path="/dopyty" element={<Navigate to="/market?demands=1" replace />} />
                <Route path="/demands" element={<Navigate to="/market?demands=1" replace />} />
                <Route path="/register" element={<Navigate to="/signup" replace />} />

                {/* Static Pages */}

                <Route path="/terms" element={<Legal type="terms" />} />
                <Route path="/legal" element={<Navigate to="/terms" replace />} />
                <Route path="/privacy" element={<Legal type="privacy" />} />
                <Route path="/cookies" element={<Legal type="cookies" />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/overenie" element={<Verification />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/sitemap-html" element={<ListingSitemap />} />

                {/* 404 */}
                <Route path="*" element={<NotFound />} />
            </Routes>
        </Suspense>
    );
};
